import { Component, computed, inject, OnInit } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';

import { BookingStore } from '../../../../pages/booking/store';

import { CurrencyExchangeUpdatedComponent } from '../../currency-exchange-updated/currency-exchange-updated.component';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  standalone: true,
  imports: [DatePipe, DecimalPipe, CurrencyExchangeUpdatedComponent],
})
export class SummaryComponent implements OnInit {
  private _bookingStore = inject(BookingStore);
  readonly selectFlightState = this._bookingStore.selectFlight;

  readonly isRateCurrency = computed(() => {
    let selectedFlight = this._bookingStore.selectFlight.selectedFlight();
    let selectedRoundTripFlight =
      this._bookingStore.selectFlight.selectedRoundTripFlight();
    let selectedSmartCombo =
      this._bookingStore.selectFlight.selectedSmartCombo();

    if (
      selectedFlight &&
      selectedFlight.flights.some((f) => f.isRateCurrency)
    ) {
      return true;
    }

    if (
      selectedRoundTripFlight &&
      selectedRoundTripFlight.flights.some((f) => f.isRateCurrency)
    ) {
      return true;
    }

    if (selectedSmartCombo && selectedSmartCombo.isRateCurrency) {
      return true;
    }

    return false;
  });

  readonly ancillaries = computed(() => {
    const bookingRequest = this._bookingStore.bookingRequest();
    if (bookingRequest) {
      const ancillaries = bookingRequest.listPassenger.flatMap(
        (p) => p.ancillaries,
      );
      return ancillaries;
    }
    return [];
  });

  readonly totalPrice = computed(() => {
    const adult = this.selectFlightState.adult();
    const child = this.selectFlightState.child();
    const infant = this.selectFlightState.infant();

    let sumPrice = 0;

    const selectedFlight = this._bookingStore.selectFlight.selectedFlight();
    if (selectedFlight) {
      if (adult > 0) {
        const adultPrice = selectedFlight.flights[0].prices.find(
          (p) => p.type.toLowerCase() === 'adt',
        );
        if (adultPrice) {
          sumPrice +=
            (adultPrice.price + adultPrice.tax + adultPrice.fee) * adult;
        }
      }

      if (child > 0) {
        const childPrice = selectedFlight.flights[0].prices.find(
          (p) => p.type.toLowerCase() === 'chd',
        );
        if (childPrice) {
          sumPrice +=
            (childPrice.price + childPrice.tax + childPrice.fee) * child;
        }
      }

      if (infant > 0) {
        const infantPrice = selectedFlight.flights[0].prices.find(
          (p) => p.type.toLowerCase() === 'inf',
        );
        if (infantPrice) {
          sumPrice +=
            (infantPrice.price + infantPrice.tax + infantPrice.fee) * infant;
        }
      }
    }

    const roundTrip = this._bookingStore.selectFlight.roundTrip();
    const selectedRoundTripFlight =
      this._bookingStore.selectFlight.selectedRoundTripFlight();
    if (roundTrip && selectedRoundTripFlight) {
      if (adult > 0) {
        const adultPrice = selectedRoundTripFlight.flights[0].prices.find(
          (p) => p.type.toLowerCase() === 'adt',
        );
        if (adultPrice) {
          sumPrice +=
            (adultPrice.price + adultPrice.tax + adultPrice.fee) * adult;
        }
      }

      if (child > 0) {
        const childPrice = selectedRoundTripFlight.flights[0].prices.find(
          (p) => p.type.toLowerCase() === 'chd',
        );
        if (childPrice) {
          sumPrice +=
            (childPrice.price + childPrice.tax + childPrice.fee) * child;
        }
      }

      if (infant > 0) {
        const infantPrice = selectedRoundTripFlight.flights[0].prices.find(
          (p) => p.type.toLowerCase() === 'inf',
        );
        if (infantPrice) {
          sumPrice +=
            (infantPrice.price + infantPrice.tax + infantPrice.fee) * infant;
        }
      }
    }

    const showSmartCombo = this._bookingStore.selectFlight.showSmartCombo();
    const selectedSmartCombo =
      this._bookingStore.selectFlight.selectedSmartCombo();
    if (showSmartCombo && selectedSmartCombo) {
      if (adult > 0) {
        const adultPrice = selectedSmartCombo.prices.find(
          (p) => p.type.toLowerCase() === 'adt',
        );
        if (adultPrice) {
          sumPrice +=
            (adultPrice.price + adultPrice.tax + adultPrice.fee) * adult;
        }
      }

      if (child > 0) {
        const childPrice = selectedSmartCombo.prices.find(
          (p) => p.type.toLowerCase() === 'chd',
        );
        if (childPrice) {
          sumPrice +=
            (childPrice.price + childPrice.tax + childPrice.fee) * child;
        }
      }

      if (infant > 0) {
        const infantPrice = selectedSmartCombo.prices.find(
          (p) => p.type.toLowerCase() === 'inf',
        );
        if (infantPrice) {
          sumPrice +=
            (infantPrice.price + infantPrice.tax + infantPrice.fee) * infant;
        }
      }
    }

    const ancillaries = this.ancillaries().reduce((total, anc) => {
      return total + anc.price;
    }, 0);

    sumPrice += ancillaries;

    return sumPrice;
  });

  readonly totalPriceChange = computed(() => {
    const bookingChecked = this._bookingStore.bookingChecked();
    let currentTotalPrice =
      bookingChecked?.flights.reduce(
        (total, flight) => total + flight.totalAmt,
        0,
      ) ?? 0;
    let newTotalPrice =
      bookingChecked?.flights.reduce((total, flight) => {
        if (flight.newTotalFare > 0) {
          return total + flight.newTotalFare + flight.totalServiceFee;
        }
        return total + flight.totalAmt;
      }, 0) ?? 0;

    const ancillaries = this.ancillaries().reduce((total, anc) => {
      return total + anc.price;
    }, 0);
    currentTotalPrice += ancillaries;
    newTotalPrice += ancillaries;

    if (bookingChecked?.flights.some((f) => f.newTotalFare > 0)) {
      return {
        current: currentTotalPrice,
        new: newTotalPrice,
      };
    }
    return null;
  });

  ngOnInit(): void {}
}
